import React, { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';

interface DonutChartProps {
  data: { name: string; value: number; color?: string }[];
  title?: string;
  centerLabel?: string;
  centerValue?: string | number;
  height?: number;
  showLegend?: boolean;
  animate?: boolean;
}

const DEFAULT_COLORS = [
  '#10b981', // green - present
  '#ef4444', // red - absent
  '#f59e0b', // amber - no response
  '#6366f1', // indigo - leave
  '#8b5cf6', // purple - holiday
];

const DonutChart: React.FC<DonutChartProps> = ({
  data,
  title,
  centerLabel,
  centerValue,
  height = 300,
  showLegend = true,
  animate = true,
}) => {
  const option = useMemo(() => {
    if (!data || data.length === 0) return {};

    const total = data.reduce((sum, item) => sum + item.value, 0);
    const chartData = data.map((item, idx) => ({
      ...item,
      color: item.color || DEFAULT_COLORS[idx % DEFAULT_COLORS.length],
    }));

    return {
      title: title ? {
        text: title,
        left: 'center',
        top: 10,
        textStyle: { fontSize: 16, fontWeight: 600, color: '#374151' }
      } : undefined,
      tooltip: {
        trigger: 'item',
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderColor: '#e5e7eb',
        borderWidth: 1,
        textStyle: { color: '#374151' },
        formatter: (params: any) => {
          const percent = ((params.value / total) * 100).toFixed(1);
          return `
            <div style="min-width: 120px;">
              <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 4px;">
                <span style="display: inline-block; width: 10px; height: 10px; border-radius: 50%; background: ${params.color};"></span>
                <span style="font-weight: 600;">${params.name}</span>
              </div>
              <div style="display: flex; justify-content: space-between;">
                <span>Count:</span>
                <span style="font-weight: 600;">${params.value.toLocaleString()}</span>
              </div>
              <div style="display: flex; justify-content: space-between;">
                <span>Percentage:</span>
                <span style="font-weight: 600;">${percent}%</span>
              </div>
            </div>
          `;
        }
      },
      legend: showLegend ? {
        orient: 'horizontal',
        bottom: 10,
        data: chartData.map(d => d.name),
        textStyle: { fontSize: 11, color: '#374151' },
        itemWidth: 12,
        itemHeight: 12,
        itemGap: 20,
      } : undefined,
      series: [{
        type: 'pie',
        radius: ['55%', '75%'],
        center: ['50%', '50%'],
        avoidLabelOverlap: true,
        itemStyle: {
          borderRadius: 4,
          borderColor: '#fff',
          borderWidth: 2,
        },
        label: {
          show: false,
        },
        emphasis: {
          label: {
            show: true,
            fontSize: 14,
            fontWeight: 'bold',
          },
          itemStyle: {
            shadowBlur: 10,
            shadowOffsetX: 0,
            shadowColor: 'rgba(0, 0, 0, 0.2)',
          },
        },
        labelLine: { show: false },
        data: chartData.map(item => ({
          value: item.value,
          name: item.name,
          itemStyle: { color: item.color },
        })),
        animationType: animate ? 'scale' : undefined,
        animationEasing: 'elasticOut',
        animationDuration: animate ? 800 : 0,
      }],
      graphic: (centerLabel || centerValue) ? [{
        type: 'group',
        left: 'center',
        top: 'center',
        children: [
          {
            type: 'text',
            style: {
              text: centerValue?.toString() || '',
              fontSize: 28,
              fontWeight: 700,
              fill: '#374151',
              textAlign: 'center',
            },
            left: 'center',
            top: -10,
          },
          {
            type: 'text',
            style: {
              text: centerLabel || '',
              fontSize: 12,
              fill: '#6b7280',
              textAlign: 'center',
            },
            left: 'center',
            top: 18,
          },
        ],
      }] : undefined,
    };
  }, [data, title, centerLabel, centerValue, showLegend, animate]);

  if (!data || data.length === 0) {
    return (
      <div 
        className="flex items-center justify-center bg-gray-50 rounded-lg border border-gray-200"
        style={{ height }}
      >
        <p className="text-gray-500">No data available</p>
      </div>
    );
  }

  return (
    <ReactECharts 
      option={option} 
      style={{ height, width: '100%' }}
      opts={{ renderer: 'svg' }}
    />
  );
};

export default DonutChart;
